import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MEDIA_STATE_LABELS, type LibraryItem } from '@jelly-dl/shared';
import { api } from '../api/client';
import { Badge, Button, Card, ErrorState, Spinner } from '../components/ui';
import { MEDIA_STATE_COLORS } from '../lib/format';

type Tab = 'all' | 'movie' | 'series';

export function Library() {
  const [tab, setTab] = useState<Tab>('all');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['library'],
    queryFn: () => api.get<{ items: LibraryItem[] }>('/api/library'),
  });

  const items = data?.items ?? [];
  const movies = items.filter((i) => i.kind === 'movie');
  const series = items.filter((i) => i.kind === 'series');
  const shown = tab === 'movie' ? movies : tab === 'series' ? series : items;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Library</h1>
        <Link to="/add">
          <Button>+ Add</Button>
        </Link>
      </div>

      <div className="flex gap-2">
        <TabButton active={tab === 'all'} onClick={() => setTab('all')}>
          All ({items.length})
        </TabButton>
        <TabButton active={tab === 'movie'} onClick={() => setTab('movie')}>
          Movies ({movies.length})
        </TabButton>
        <TabButton active={tab === 'series'} onClick={() => setTab('series')}>
          TV ({series.length})
        </TabButton>
      </div>

      {isLoading && <Spinner />}
      {isError && <ErrorState message="Couldn't load your library." onRetry={() => refetch()} />}

      {data && items.length === 0 && (
        <Card className="text-center text-slate-400">
          Nothing here yet.{' '}
          <Link to="/add" className="text-indigo-300 underline">
            Add a movie or show
          </Link>{' '}
          to start monitoring it.
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {shown.map((item) => (
          <PosterCard key={`${item.kind}:${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-md bg-indigo-600/20 px-3 py-1.5 text-sm text-indigo-200'
          : 'rounded-md px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800'
      }
    >
      {children}
    </button>
  );
}

function PosterCard({ item }: { item: LibraryItem }) {
  const to = item.kind === 'movie' ? `/movie/${item.id}` : `/series/${item.id}`;
  return (
    <Link to={to} className="group">
      <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/60 transition group-hover:border-indigo-600/60">
        <div className="aspect-[2/3] w-full bg-slate-800">
          {item.posterUrl ? (
            <img src={item.posterUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-slate-500">
              {item.title}
            </div>
          )}
        </div>
        <div className="space-y-1 p-2">
          <div className="truncate text-sm font-medium text-slate-100" title={item.title}>
            {item.title}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{item.year ?? '—'}</span>
            {item.kind === 'movie' && item.state ? (
              <Badge className={MEDIA_STATE_COLORS[item.state]}>{MEDIA_STATE_LABELS[item.state]}</Badge>
            ) : (
              <span className="text-xs uppercase text-slate-500">{item.kind === 'series' ? 'TV' : 'Movie'}</span>
            )}
          </div>
          {!item.monitored && <div className="text-xs text-slate-500">paused</div>}
        </div>
      </div>
    </Link>
  );
}
